/**
 * Tipos para el módulo de Pedidos a Proveedores de Athenea
 */

export type SupplierOrderStatus =
  | 'draft'      // Borrador - no enviado
  | 'pending'    // Pendiente de envío
  | 'sent'       // Enviado al proveedor
  | 'partial'    // Recepción parcial
  | 'received'   // Completamente recibido
  | 'invoiced'   // Facturado/Vinculado a gasto
  | 'cancelled'; // Cancelado

export interface SupplierOrder {
  id: string;
  order_number: string;

  // Proveedor
  supplier_id?: string;
  supplier_name: string;
  supplier_tax_id?: string;
  supplier_email?: string;
  supplier_phone?: string;

  // Estado y fechas
  status: SupplierOrderStatus;
  order_date: string;
  expected_date?: string;
  sent_date?: string;
  received_date?: string;

  // Importes
  subtotal: number;
  tax_amount: number;
  total_amount: number;

  // Notas
  notes?: string;
  internal_notes?: string;

  // Vinculación con gasto/factura de proveedor
  linked_expense_id?: string;

  // Relaciones
  lines?: SupplierOrderLine[];
  supplier?: {
    id?: string;
    legal_name?: string;
    tax_id?: string;
    email?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    postal_code?: string;
    city?: string;
    province?: string;
    contact_name?: string;
  };

  // Metadatos
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface SupplierOrderLine {
  id: string;
  order_id: string;
  line_number: number;

  // Referencia a material (opcional)
  material_id?: string;
  raw_material_id?: string;
  billing_article_id?: string;

  // Datos del artículo
  code?: string;
  description: string;
  unit: string;

  // Cantidades
  quantity_ordered: number;
  quantity_received: number;
  unit_price: number;

  // Totales
  subtotal: number;
  discount_percent: number;
  tax_rate: number;
  tax_amount: number;
  total: number;

  // Estado
  is_fully_received: boolean;

  // Notas
  notes?: string;

  // Relaciones expandidas
  material?: {
    id: string;
    name: string;
    code?: string;
    current_stock?: number;
    min_stock?: number;
  };
}

export interface StockAlert {
  id: string;
  material_type: 'inventory' | 'raw';
  material_id: string;
  material_name: string;
  material_code?: string;
  current_stock: number;
  min_stock: number;
  quantity_needed: number;
  suggested_supplier_id?: string;
  suggested_supplier_name?: string;
  is_dismissed: boolean;
  created_at: string;
}

export interface SupplierOrderFormData {
  supplier_id?: string;
  supplier_name: string;
  supplier_tax_id?: string;
  order_date: string;
  expected_date?: string;
  notes?: string;
  lines: SupplierOrderLineFormData[];
}

export interface SupplierOrderLineFormData {
  id?: string;
  material_id?: string;
  raw_material_id?: string;
  code?: string;
  description: string;
  unit: string;
  quantity_ordered: number;
  unit_price: number;
  discount_percent: number;
  tax_rate: number;
}

// Etiquetas de estado
export const SUPPLIER_ORDER_STATUS_LABELS: Record<SupplierOrderStatus, string> = {
  draft: 'Borrador',
  pending: 'Pendiente',
  sent: 'Enviado',
  partial: 'Parcial',
  received: 'Recibido',
  invoiced: 'Facturado',
  cancelled: 'Cancelado',
};

export const SUPPLIER_ORDER_STATUS_COLORS: Record<SupplierOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-amber-100 text-amber-700',
  sent: 'bg-blue-100 text-blue-700',
  partial: 'bg-purple-100 text-purple-700',
  received: 'bg-green-100 text-green-700',
  invoiced: 'bg-indigo-100 text-indigo-700',
  cancelled: 'bg-red-100 text-red-700',
};

// Transiciones de estado válidas
export const SUPPLIER_ORDER_TRANSITIONS: Record<SupplierOrderStatus, SupplierOrderStatus[]> = {
  draft: ['pending', 'cancelled'],
  pending: ['sent', 'cancelled'],
  sent: ['partial', 'received', 'cancelled'],
  partial: ['received', 'cancelled'],
  received: ['invoiced'],
  invoiced: [],
  cancelled: [],
};

export function canTransitionTo(
  currentStatus: SupplierOrderStatus,
  targetStatus: SupplierOrderStatus
): boolean {
  return SUPPLIER_ORDER_TRANSITIONS[currentStatus]?.includes(targetStatus) ?? false;
}
